import { isDesktop } from "@utils/isDesktop";
import { handleCustomUDL } from "@utils/UDLUtils";
import { setupScrollSpy } from "./utils/setupScrollSpy";

// ─── Constants ────────────────────────────────────────────────────────────────

const CLASSES = {
  open: "is-open",
  animationStart: "animation-start",
  activeOnly: "active",
};

const SELECTORS = {
  serviceNavigation: ".enh-nav__service-navigation",
  dialog: ".enh-nav__dialog",
  dialogContent: ".enh-nav__dialog-content",
  lvl1Link: ".enh-nav__link--primary",
  toggleMobileNavBtn: ".enh-nav__button--hamburger",
  search: ".js-site-search a",
  overlay: ".enh-nav__body-overlay",
  // document-scoped — may live outside the component root
  logo: ".comp-logo a",
  focusableElements:
    ":where(a[href],area[href],input,select,textarea,button,iframe,object,embed,[tabindex],[contenteditable]):not([tabindex='-1'],[disabled])",
};

// ─── Class ────────────────────────────────────────────────────────────────────

export class MicroHamburgerNavigation {
  private el: HTMLElement;
  private abortController: AbortController;

  private isLandingNavType: boolean;
  private isMicroNavType: boolean;

  // Cached elements
  private logo: HTMLAnchorElement | null;
  private toggleMobileNavBtns: HTMLElement[];
  private overlay: HTMLElement | null;
  private dialog: HTMLElement | null;
  private dialogContent: HTMLElement | null;
  private serviceNavigation: HTMLElement | null;
  private mainLinks: HTMLAnchorElement[];
  private searchLinks: HTMLAnchorElement[];

  constructor(el: HTMLElement) {
    this.el = el;
    this.abortController = new AbortController();

    this.isLandingNavType =
      el.parentElement?.classList.contains("enh-nav__landing-navigation") ?? false;
    this.isMicroNavType =
      el.parentElement?.classList.contains("enh-nav__microsite-navigation") ?? false;

    // document-scoped — may live outside the component root
    this.logo = document.querySelector<HTMLAnchorElement>(SELECTORS.logo);

    this.toggleMobileNavBtns = Array.from(
      el.querySelectorAll<HTMLElement>(SELECTORS.toggleMobileNavBtn),
    );
    this.overlay = el.querySelector<HTMLElement>(SELECTORS.overlay);
    this.dialog = el.querySelector<HTMLElement>(SELECTORS.dialog);
    this.dialogContent = el.querySelector<HTMLElement>(SELECTORS.dialogContent);
    this.serviceNavigation = el.querySelector<HTMLElement>(SELECTORS.serviceNavigation);
    // scoped to dialog to mirror jQuery's this.$dialog.find(selectors.lvl1Link)
    this.mainLinks = Array.from(
      this.dialog?.querySelectorAll<HTMLAnchorElement>(SELECTORS.lvl1Link) ?? [],
    );
    this.searchLinks = Array.from(
      el.querySelectorAll<HTMLAnchorElement>(SELECTORS.search),
    );
  }

  init(): void {
    this.bindEvents();
  }

  destroy(): void {
    this.abortController.abort();
  }

  // ─── Event binding ──────────────────────────────────────────────────────────

  private bindEvents(): void {
    const { signal } = this.abortController;

    if (this.isLandingNavType) {
      this.setupNavigationScrollSpy(signal);
    }

    this.bindToggleHamburger(signal);
    this.bindEscapeKeyPress(signal);
    this.bindFocusTraps(signal);
  }

  // ─── Scroll spy (landing nav only) ───────────────────────────────────────────

  private setupNavigationScrollSpy(signal: AbortSignal): void {
    setupScrollSpy(this.mainLinks, signal);
  }

  // ─── Toggle hamburger visibility ─────────────────────────────────────────────

  private bindToggleHamburger(signal: AbortSignal): void {
    this.toggleMobileNavBtns.forEach((btn) => {
      btn.addEventListener(
        "click",
        () => {
          if (btn.classList.contains(CLASSES.open)) {
            this.closeDialog();
            this.hideBodyOverlay();
          } else {
            this.showBodyOverlay();
            this.openDialog();
          }
        },
        { signal },
      );
    });

    this.overlay?.addEventListener(
      "click",
      () => {
        this.closeDialog();
        this.hideBodyOverlay();
      },
      { signal },
    );

    // Clicking a level-1 link collapses the menu
    this.mainLinks.forEach((link) => {
      link.addEventListener(
        "click",
        () => {
          this.closeDialog();
          this.hideBodyOverlay();
        },
        { signal },
      );
    });
  }

  // ─── Body overlay ────────────────────────────────────────────────────────────

  private showBodyOverlay(): void {
    document.body.style.overflow = "hidden";
    this.serviceNavigation?.classList.add(CLASSES.open, CLASSES.animationStart);
  }

  private hideBodyOverlay(): void {
    document.body.style.overflow = "initial";
    this.serviceNavigation?.classList.remove(CLASSES.animationStart, CLASSES.open);
  }

  // ─── Escape key ──────────────────────────────────────────────────────────────

  private bindEscapeKeyPress(signal: AbortSignal): void {
    this.el.addEventListener(
      "keydown",
      (e: KeyboardEvent) => {
        if (e.code === "Escape" && this.isDialogOpen()) {
          this.closeDialog();
          this.hideBodyOverlay();
          this.logo?.focus();
        }
      },
      { signal },
    );
  }

  // ─── Focus traps ─────────────────────────────────────────────────────────────

  private bindFocusTraps(signal: AbortSignal): void {
    const lastFocusableInDialog = this.dialog
      ? this.getFocusableElement(this.dialog, "last")
      : null;
    const lastLinkInContent = this.dialogContent
      ? this.getFocusableElement(this.dialogContent, "last")
      : null;

    // Shift+Tab on a search link (mobile) → wrap to last focusable in dialog
    this.searchLinks.forEach((searchLink) => {
      searchLink.addEventListener(
        "keydown",
        (e: KeyboardEvent) => {
          if (
            e.code === "Tab" &&
            this.isDialogOpen() &&
            e.shiftKey === true &&
            !isDesktop()
          ) {
            e.preventDefault();
            lastFocusableInDialog?.focus();
          }
        },
        { signal },
      );
    });

    // Tab (no shift) on last focusable in dialog → wrap back to search
    if (lastFocusableInDialog) {
      lastFocusableInDialog.addEventListener(
        "keydown",
        (e: KeyboardEvent) => {
          if (e.code === "Tab" && this.isDialogOpen() && e.shiftKey === false) {
            e.preventDefault();
            this.searchLinks[0]?.focus();
          }
        },
        { signal },
      );
    }

    // Tab (no shift) on last link in dialog content (desktop) → wrap to logo
    if (lastLinkInContent) {
      lastLinkInContent.addEventListener(
        "keydown",
        (e: KeyboardEvent) => {
          if (
            e.code === "Tab" &&
            this.isDialogOpen() &&
            e.shiftKey === false &&
            isDesktop()
          ) {
            e.preventDefault();
            this.logo?.focus();
          }
        },
        { signal },
      );
    }

    // Shift+Tab on logo (desktop) → wrap to last link in dialog content
    if (this.logo) {
      this.logo.addEventListener(
        "keydown",
        (e: KeyboardEvent) => {
          if (
            e.code === "Tab" &&
            this.isDialogOpen() &&
            e.shiftKey === true &&
            isDesktop()
          ) {
            e.preventDefault();
            lastLinkInContent?.focus();
          }
        },
        { signal },
      );
    }
  }

  // ─── Open / close dialog ─────────────────────────────────────────────────────

  private openDialog(): void {
    const isLandingOrMicrositeNav = this.isLandingNavType || this.isMicroNavType;
    const timeout = isDesktop() && isLandingOrMicrositeNav ? 0 : 1;

    this.toggleMobileNavBtns.forEach((btn) => {
      btn.classList.add(CLASSES.open);
      btn.setAttribute("aria-expanded", "true");
    });

    this.dialog?.classList.add(CLASSES.open);
    if (this.dialogContent) this.dialogContent.scrollTop = 0;
    this.startAnimation(this.dialog, timeout);
    document.body.style.overflow = "hidden";

    if (isLandingOrMicrositeNav) {
      this.handleHamburgerUDL("open", "expanded");
    }
  }

  private closeDialog(): void {
    const isLandingOrMicrositeNav = this.isLandingNavType || this.isMicroNavType;
    const timeout = isDesktop() && isLandingOrMicrositeNav ? 0 : 400;

    this.toggleMobileNavBtns.forEach((btn) => {
      btn.classList.remove(CLASSES.open);
      btn.setAttribute("aria-expanded", "false");
    });

    setTimeout(() => {
      this.dialog?.classList.remove(CLASSES.open);
    }, timeout);

    this.dialog?.classList.remove(CLASSES.animationStart);
    document.body.style.overflow = "initial";

    if (isLandingOrMicrositeNav) {
      this.handleHamburgerUDL("close", "collapsed");
    }
  }

  // ─── Dialog state ────────────────────────────────────────────────────────────

  private isDialogOpen(): boolean {
    return this.dialog?.classList.contains(CLASSES.open) ?? false;
  }

  // ─── Animation helper ────────────────────────────────────────────────────────

  private startAnimation(elem: HTMLElement | null, delay: number = 1): void {
    setTimeout(() => {
      elem?.classList.add(CLASSES.animationStart);
    }, delay);
  }

  // ─── UDL ─────────────────────────────────────────────────────────────────────

  private handleHamburgerUDL(action: string, state: string): void {
    const windowWidth = document.documentElement.clientWidth;
    const isMobile = windowWidth < 768;
    const isTablet = windowWidth >= 768 && windowWidth < 1024;
    const deviceType = isMobile ? "mobile" : isTablet ? "tablet" : "desktop";

    handleCustomUDL("hamburger_navigation_interaction", {
      action,
      menu_state: state,
      device_type: deviceType,
    });
  }

  // ─── Focus helpers ────────────────────────────────────────────────────────────

  private getFocusableElement(
    root: HTMLElement,
    order: "first" | "last",
  ): HTMLElement | null {
    const focusableElements = Array.from(
      root.querySelectorAll<HTMLElement>(SELECTORS.focusableElements),
    );
    return order === "first"
      ? (focusableElements.at(0) ?? null)
      : (focusableElements.at(-1) ?? null);
  }

  // ─── Static bootstrap ───────────────────────────────────────────────────────

  static initAll(root: Document | HTMLElement = document): void {
    root
      .querySelectorAll<HTMLElement>('[data-component="micro-hamburger-navigation"]')
      .forEach((el) => {
        new MicroHamburgerNavigation(el).init();
      });
  }
}
